import { useReducer } from "react";
import { MessageDescriptor } from "@lingui/core";
import { Error } from "../fetch/useFetch";
import { ResultError } from "../../Result";
import { driverFacingErrorMessage, driverFacingResultError } from "../../userFacingMessages/userFacingMessages";
import { UserFacingMessageCode } from "../../userFacingMessages/userFacingMessageCodes";
import { useGetReprintStatus } from "../../../requests/useGetReprintStatus";
import useRequestReprint from "../../../requests/useRequestReprint";
import useInterval from "./useInterval";

const reprintRefreshTimeMs = 3000;

export type ReprintState =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'succeeded' }
  | { status: 'failed', message: MessageDescriptor, retryable: boolean };

type ReprintAction =
  | { type: 'Requested' }
  | { type: 'RequestFailed', error: Error }
  | { type: 'StatusReceived', succeeded: boolean | null, error: ResultError | null };

function reprintReducer(state: ReprintState, action: ReprintAction): ReprintState {
  switch (action.type) {
    case 'Requested':
      return { status: 'pending' };

    case 'RequestFailed':
      return {
        status: 'failed',
        message: driverFacingErrorMessage(action.error),
        retryable: action.error.errorCode !== UserFacingMessageCode.ReprintWindowExpired,
      };

    case 'StatusReceived':
      if (state.status !== 'pending') return state;
      if (action.succeeded === true) return { status: 'succeeded' };
      if (action.succeeded === false && action.error)
        return { status: 'failed', message: driverFacingResultError(action.error), retryable: false };
      return state;

    default:
      return state;
  }
}

const useTicketReprint = (stagedOrderId: string) => {
  const [state, dispatch] = useReducer(reprintReducer, { status: 'idle' });

  const statusQuery = useGetReprintStatus(stagedOrderId, {
    autoQuery: false,
    onSuccess: status => dispatch({ type: 'StatusReceived', succeeded: status.succeeded, error: status.error }),
  });

  const request = useRequestReprint(stagedOrderId, {
    onSuccess: () => dispatch({ type: 'Requested' }),
    onError: error => dispatch({ type: 'RequestFailed', error }),
  });

  useInterval(() => {
    if (state.status !== 'pending') return;
    if (statusQuery.isLoading) return;

    statusQuery.query();
  }, reprintRefreshTimeMs, false);

  const requestReprint = () => {
    if (request.isLoading || state.status === 'pending') return;

    request.request();
  };

  return { state, isRequesting: request.isLoading, requestReprint };
};

export default useTicketReprint;
